import DividerWithText from '@/components/common/DividerWithText';
import FadeInBox, { FadeInBoxProps } from '@/components/common/FadeInBox';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import PlentyLogo from '@/components/common/PlentyLogo';
import { BigHeader, Paragraph } from '@/components/common/Typography';
import { useTrackPageViewed } from '@/services/analytics/useTracking';
import { Link } from '@chakra-ui/react';
import {
    APPLE_APP_STORE_URL,
    GOOGLE_PLAY_STORE_URL,
} from '@common/appStoreLinks';
import appleAppStoreLogo from '/public/images/apple-app-store-logo.svg';
import googleAppStoreLogo from '/public/images/google-app-store-logo.png';
import Image from 'next/image';
import NextLink from 'next/link';
import { useIsMobile } from './hooks/useIsMobile';

interface MobileSplashProps extends FadeInBoxProps {
    children?: React.ReactNode;
}

export const MobileSplash = ({ children }: MobileSplashProps) => {
    const isMobile = useIsMobile();

    if (isMobile) {
        return <MobileSplashView />;
    }

    return children;
};

const MobileSplashView = () => {
    useTrackPageViewed('Mobile Splash', { mobile: true });

    return (
        <FadeInBox
            pos="fixed"
            display="flex"
            left={0}
            top={0}
            w="100vw"
            h="100%"
            bg="white url(/images/shared/radial-gradient.png) 50% 180%/contain no-repeat"
            zIndex={50}
            flexDirection="column"
            alignItems="center"
        >
            <FlexCol
                w="full"
                alignItems="center"
                justifyContent="center"
                mt={20}
            >
                <PlentyLogo width={78} textPosition="vertical" />
                <DownloadMobileApp />
                <DividerWithText text="or" w="full" p={8} />
                <Paragraph maxW={250} textAlign="center" color="textMuted">
                    Head to{' '}
                    <strong>
                        <Link color="blue.500">app.withplenty.com</Link>
                    </strong>{' '}
                    on a <strong>desktop or tablet</strong> to continue.
                </Paragraph>
            </FlexCol>
        </FadeInBox>
    );
};

const DownloadMobileApp = () => (
    <>
        <BigHeader
            maxW={250}
            mt={9}
            textAlign="center"
            fontSize="5xl"
            fontWeight="medium"
            fontFamily="serif"
            color="black"
            lineHeight={1.1}
        >
            Your dashboard <br /> is ready.
        </BigHeader>
        <Paragraph maxW={250} textAlign="center" color="textMuted" mt={6}>
            Download the mobile app to manage your finances on the go.
        </Paragraph>
        <FlexRow gap={4} mt={8}>
            <NextLink href={APPLE_APP_STORE_URL}>
                <Image
                    src={appleAppStoreLogo}
                    alt="Apple App Store"
                    height={40}
                />
            </NextLink>
            <NextLink href={GOOGLE_PLAY_STORE_URL}>
                <Image
                    src={googleAppStoreLogo}
                    alt="Google Play Store"
                    height={40}
                />
            </NextLink>
        </FlexRow>
    </>
);
